import { RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ErrorCard, Td, Th, Verdict, formatDate, formatNumber, formatPercent, signColor } from '../components/research';
import { usePrograms } from '../hooks/usePrograms';

export default function Programs() {
    const { programs, isLoading, error, refresh } = usePrograms();

    const active = programs.filter((a) => a.is_active);
    const withRuns = programs.filter((a) => a.run_count > 0).length;

    return (
        <div className="flex min-h-[calc(100vh-112px)] min-w-0 flex-col gap-4">
            <section className="flex flex-wrap items-center justify-between gap-3 px-1">
                <div>
                    <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">Programs</h2>
                    <p className="mt-1 text-sm leading-6 sm:text-base" style={{ color: 'var(--foreground-muted)' }}>
                        {active.length} active · {withRuns} with recorded results. Headline is the newest run on a sample that
                        carries the project bar, falling back to the newest run of any kind.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={refresh}
                    disabled={isLoading}
                    className="button-secondary inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium"
                >
                    <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} aria-hidden="true" />
                    Refresh
                </button>
            </section>

            {error ? <ErrorCard message={error} onRetry={refresh} /> : null}

            <div className="surface-card min-h-0 flex-1 rounded-[28px] p-6 sm:p-7">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[860px] border-collapse text-sm">
                        <thead>
                            <tr style={{ color: 'var(--foreground-muted)' }}>
                                <Th>Program</Th>
                                <Th>Strategies</Th>
                                <Th align="right">Instruments</Th>
                                <Th align="right">Runs</Th>
                                <Th>Latest sample</Th>
                                <Th align="right">Net</Th>
                                <Th align="right">Sharpe</Th>
                                <Th align="right">Trades</Th>
                                <Th>Verdict</Th>
                                <Th align="right">Last run</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {programs.map((program) => {
                                const strategies = (program.mandates ?? []).map((m) => m.strategy);
                                return (
                                    <tr key={program.key} className="task-row">
                                        <Td>
                                            <Link to={`/programs/${program.key}`} className="font-medium hover:underline">
                                                {program.key}
                                            </Link>
                                            {!program.is_active ? (
                                                <span className="ml-2 text-xs" style={{ color: 'var(--foreground-muted)' }}>
                                                    retired
                                                </span>
                                            ) : null}
                                        </Td>
                                        <Td color="var(--foreground-muted)">{strategies.join(' + ') || '—'}</Td>
                                        <Td align="right" mono color="var(--foreground-muted)">
                                            {program.instrument_count}
                                        </Td>
                                        <Td align="right" mono color="var(--foreground-muted)">
                                            {program.run_count}
                                        </Td>
                                        <Td color="var(--foreground-muted)">
                                            {program.latest_sample ?? '—'}
                                            {program.latest_num_windows ? (
                                                <span className="ml-1 text-xs opacity-70">({program.latest_num_windows}w)</span>
                                            ) : null}
                                        </Td>
                                        <Td align="right" mono color={signColor(program.latest_sum_pct)}>
                                            {formatPercent(program.latest_sum_pct)}
                                        </Td>
                                        <Td align="right" mono color={signColor(program.latest_sharpe)}>
                                            {formatNumber(program.latest_sharpe)}
                                        </Td>
                                        <Td align="right" mono color="var(--foreground-muted)">
                                            {program.latest_num_trades ?? '—'}
                                        </Td>
                                        <Td>{program.run_count > 0 ? <Verdict passed={program.latest_passed} /> : '—'}</Td>
                                        <Td align="right" color="var(--foreground-muted)">
                                            {formatDate(program.last_run_at)}
                                        </Td>
                                    </tr>
                                );
                            })}
                            {!isLoading && programs.length === 0 && !error ? (
                                <tr>
                                    <td colSpan={10} className="py-8 text-center" style={{ color: 'var(--foreground-muted)' }}>
                                        No programs registered yet. Run{' '}
                                        <code>python scripts/apply_research_schema.py</code> in the backend.
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
